import { memo, useCallback, useMemo, useState } from 'react';
import PropertyInputPhone, { getPhoneNumberString } from './PropertyInputPhone';
import UIEditableControls from 'UIComponents/editable/UIEditableControls';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { EERIE } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIPopoverCloseButton from 'UIComponents/tooltip/UIPopoverCloseButton';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import usePhoneNumberValidator, { FETCH_STATUS } from './phone/hooks/usePhoneNumberValidator';
import getIn from 'transmute/getIn';
import { formatRawPhoneNumber, getCountryDialCode, isValidRawPhoneNumber } from './phone/utils/phoneNumberUtils';
import styled from 'styled-components';
import { getSetting, setSetting } from './phone/utils/localSetting';
import { COUNTRY_DIAL_CODE_LOCAL_SETTINGS_KEY, SHOW_PHONE_TEXT_INPUT_LOCAL_SETTINGS_KEY } from './phone/constants/localSettingConstants';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledUIEditableControls = styled(UIEditableControls).withConfig({
  displayName: "PropertyInputPhoneWrapper__StyledUIEditableControls",
  componentId: "ukx4fr-0"
})(["margin:0 !important;"]);
const PopoverBody = ({
  onCancel,
  onConfirm,
  disabled,
  initialValue,
  onTracking,
  showAutoSaveText
}) => {
  const [newValue, setNewValue] = useState(initialValue || '');
  const [country, setCountry] = useState(undefined);
  const initialCountryDialCode = useMemo(() => getSetting(COUNTRY_DIAL_CODE_LOCAL_SETTINGS_KEY) || '', []);
  const shouldDefaultToTextInput = useMemo(() => getSetting(SHOW_PHONE_TEXT_INPUT_LOCAL_SETTINGS_KEY) === 'true', []);
  // PhoneNumberInput cannot display non-digits, instead show Text input when the initialValue is invalid
  // Or if the user had most recently used the text input to save a number
  const [isTextInput, setIsTextInput] = useState(!isValidRawPhoneNumber(newValue) || shouldDefaultToTextInput);
  const {
    status,
    data,
    error
  } = usePhoneNumberValidator(newValue);
  const isValid = useMemo(() => getIn(['isValid'], data), [data]);
  const handlePhoneNumberInputChange = useCallback((value, newCountry = undefined) => {
    setNewValue(value);
    setCountry(newCountry);
  }, []);
  const handleToggleInputTracking = useCallback(() => {
    const initialIsTextInput = !isValidRawPhoneNumber(initialValue || '') || shouldDefaultToTextInput;
    if (typeof onTracking === 'function' && isTextInput !== initialIsTextInput) {
      if (isTextInput) {
        onTracking('removes phone number formatting in phone property input');
      } else {
        onTracking('applies phone number formatting in phone property input');
      }
    }
  }, [onTracking, isTextInput, initialValue, shouldDefaultToTextInput]);
  const handleToggleInput = useCallback(() => {
    if (error) {
      return;
    }
    if (!data) {
      setNewValue('');
      return;
    }
    if (isTextInput) {
      let formattedNumber = data.formattedNumber;

      // Mimics behavior from ui-addon-i18n/components/PhoneNumberInput
      // https://git.hubteam.com/HubSpot/ui-addon-i18n/blob/master/static/js/components/PhoneNumberInput.js#L190
      if (formattedNumber && formattedNumber.indexOf('+') === 0) {
        formattedNumber = `+${formattedNumber.replace(/\D/g, '')}`;
      }
      const newPhoneNumber = getPhoneNumberString(formattedNumber, data.extension);
      setNewValue(newPhoneNumber);
    } else {
      setNewValue(data.rawNumber || '');
    }
    setIsTextInput(isText => !isText);
  }, [data, error, isTextInput]);
  const handleSave = useCallback(() => {
    onConfirm(newValue, country, isTextInput);
    handleToggleInputTracking();
  }, [newValue, country, isTextInput, onConfirm, handleToggleInputTracking]);
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPhone.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPhone.apply"
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: "display-flex",
      children: [/*#__PURE__*/_jsx(UIFormLabel, {
        className: "p-left-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPhone.label"
        })
      }), /*#__PURE__*/_jsx(UIPopoverCloseButton, {
        color: EERIE,
        onClick: onCancel,
        className: "m-left-auto"
      })]
    }), /*#__PURE__*/_jsx(StyledUIEditableControls, {
      className: "m-bottom-1 p-x-4 p-bottom-4",
      onCancel: onCancel,
      onSave: handleSave,
      saveDisabled: disabled || !isTextInput && (status === FETCH_STATUS.PENDING || !isValid) || initialValue === newValue,
      use: "flush",
      defaultOpen: true,
      saveLabel: confirmLabel,
      children: /*#__PURE__*/_jsx(PropertyInputPhone, {
        disabled: disabled,
        value: newValue,
        initialCountryDialCode: initialCountryDialCode,
        status: status,
        error: !!error || !isValid && !!data,
        showTextInput: isTextInput,
        onChange: handlePhoneNumberInputChange,
        onToggleInput: handleToggleInput
      })
    })]
  });
};
const PropertyInputPhoneWrapper = props => {
  const {
    disabled,
    inputRef,
    onChange,
    onFocus,
    onTracking,
    popoverOpen,
    popoverProps,
    readOnly,
    setPopoverOpen,
    value,
    showAutoSaveText = false
  } = props;
  const onCancel = useCallback(() => {
    setPopoverOpen(false);
  }, [setPopoverOpen]);
  const onConfirm = useCallback((newValue, country, isTextInput) => {
    onChange(SyntheticEvent(newValue));
    if (country) {
      const countryDialCode = getCountryDialCode(country);
      setSetting(COUNTRY_DIAL_CODE_LOCAL_SETTINGS_KEY, countryDialCode ? `+${countryDialCode}` : '');
    }
    setSetting(SHOW_PHONE_TEXT_INPUT_LOCAL_SETTINGS_KEY, `${isTextInput}`);
    setPopoverOpen(false);
  }, [onChange, setPopoverOpen]);
  const formattedValue = useMemo(() => {
    // null and undefined do not clear the UIExpandingTextArea
    if (value === null || value === undefined) {
      return '';
    }
    return formatRawPhoneNumber(value);
  }, [value]);
  return /*#__PURE__*/_jsx(UIPopover, Object.assign({
    closeOnOutsideClick: true,
    placement: "right"
  }, popoverProps, {
    open: popoverOpen && !readOnly && !disabled,
    onOpenChange: evt => setPopoverOpen(evt.target.value),
    content: {
      body: /*#__PURE__*/_jsx(PopoverBody, {
        onCancel: onCancel,
        onConfirm: onConfirm,
        disabled: disabled,
        initialValue: value,
        onTracking: onTracking,
        showAutoSaveText: showAutoSaveText
      })
    },
    "data-test-id": "property-input-phone-popover",
    children: /*#__PURE__*/_jsx(UIExpandingTextArea, Object.assign({}, getTransferrableProps(props), {
      value: formattedValue,
      inputRef: inputRef,
      shrink: true,
      onChange: undefined,
      onFocus: evt => {
        if (onFocus) {
          onFocus(evt);
        }
        evt.target.blur();
        setPopoverOpen(true);
      },
      readOnly: readOnly,
      disabled: disabled,
      "data-test-id": "property-input-phone-button"
    }))
  }));
};
export default /*#__PURE__*/memo(PropertyInputPhoneWrapper);